import { useCallback, useContext } from 'react';
import { matchPath } from 'react-router-dom';
import { useGeneralRoutes } from '@namespace/cms/src/hooks/useGeneralRoutes';
import { useOtherSettings } from '@namespace/cms/src/hooks/useOtherSettings';
import { USER_ROLES } from '@namespace/user/src/constants';
import { UserContext } from '@namespace/user/src/context';
import { AdditionalRoutes, LogAmplitudeParams } from './types';

const useLogAmplitude = (): ((
  params: LogAmplitudeParams
) => Promise<null | void>) => {
  const [user] = useContext(UserContext);
  const { role } = user;
  const { amplitude } = useOtherSettings();
  const { web } = amplitude;
  const { whitelist } = web;
  const {
    preMatchLobby,
    liveSport,
    games,
    casinoLive,
    instantGames,
    navLobby,
    promotion,
    tournament
  } = useGeneralRoutes();
  const generalRoutes = {
    prematch: preMatchLobby,
    casino: games,
    'live-casino': casinoLive,
    lobby: navLobby,
    'instant-games': instantGames,
    'live-sport': liveSport,
    promotions: promotion,
    tournaments: tournament
  };

  const findMatchingRoute = (
    path: string,
    additionalRoutes: AdditionalRoutes = {}
  ): string | null => {
    const routes = Object.entries({ ...generalRoutes, ...additionalRoutes });

    for (const [key, routePaths] of routes) {
      const pathsArray = Array.isArray(routePaths) ? routePaths : [routePaths];

      for (const routePath of pathsArray) {
        const formattedRoutePath = `${routePath?.split('/')[1]}/`;

        if (formattedRoutePath === '/') {
          if (path === '/') {
            return key;
          }
        } else if (matchPath({ path: formattedRoutePath, end: false }, path)) {
          return key;
        }
      }
    }

    return null;
  };

  return useCallback(
    ({
      actionName,
      attributes: attr = {},
      isMatchPath,
      isMatchSection,
      additionalRoutes
    }: LogAmplitudeParams): Promise<null | void> => {
      return new Promise((resolve) => {
        const isValidAction = whitelist.some((action) => {
          const isActionForGuest = action?.guest === true;
          const isActionWhitelisted = action?.event === actionName;
          return role === USER_ROLES.GUEST
            ? isActionForGuest && isActionWhitelisted
            : isActionWhitelisted;
        });

        const pathParts = window.location.pathname
          .split('/')
          .filter((part) => part?.length);
        const formattedPathname =
          pathParts?.length >= 2 ? `/${pathParts[1]}` : '/';
        const matchingRoute =
          isMatchPath && findMatchingRoute(formattedPathname, additionalRoutes);
        const matchingSection =
          isMatchSection && findMatchingRoute(attr?.section_open as string);
        const attributes = attr;

        if (matchingRoute) {
          attributes.section_name = matchingRoute;
        }

        if (isMatchSection) {
          if (matchingSection) {
            attributes.section_open = matchingSection;
          } else {
            return null; // don't send action if section not matching
          }
        }
        if (isValidAction && window.amplitude) {
          window.amplitude.track(actionName, attributes).promise.then(resolve);
        } else {
          resolve(null);
        }

        return null;
      });
    },
    [role, whitelist]
  );
};

export default useLogAmplitude;
